import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Sparkles, Wand2, RefreshCw, Copy, Check, ChevronDown, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AI Writing Assistant for Organiser Portal
 * Helps organisers write better event descriptions
 */
export default function AIWritingAssistant({ 
  eventId, 
  token, 
  currentDescription,
  onDescriptionGenerated,
  onSocialGenerated
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  
  // Description generator state
  const [bulletPoints, setBulletPoints] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [generatedDescription, setGeneratedDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  
  // Social posts state
  const [socialPosts, setSocialPosts] = useState(null);
  const [generatingSocial, setGeneratingSocial] = useState(false);
  
  // Usage tracking
  const [usage, setUsage] = useState(null);
  const [copied, setCopied] = useState({});
  const [error, setError] = useState(null);

  const handleGenerateDescription = async () => {
    if (!bulletPoints.trim()) {
      toast.error('Please enter some bullet points');
      return;
    }
    
    setGenerating(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/organiser/ai/generate-description?eventId=${eventId}&token=${token}`,
        { bulletPoints, tone, length }
      );
      
      setGeneratedDescription(response.data.description);
      setUsage(response.data.usage);
      toast.success('Description generated!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to generate description';
      setError(message);
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateSocial = async () => {
    setGeneratingSocial(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/organiser/ai/generate-social?eventId=${eventId}&token=${token}`,
        { platforms: ['linkedin', 'twitter'], highlights: currentDescription }
      );
      
      setSocialPosts(response.data);
      setUsage(response.data.usage);
      toast.success('Social posts generated!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to generate social posts';
      setError(message);
      toast.error(message);
    } finally {
      setGeneratingSocial(false);
    }
  };

  const handleUseDescription = () => {
    if (onDescriptionGenerated && generatedDescription) {
      onDescriptionGenerated(generatedDescription);
      toast.success('Description applied!');
    }
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
  };

  const handleImproveExisting = () => {
    if (currentDescription) {
      setBulletPoints(`• ${currentDescription.split('. ').join('\n• ')}`);
      setActiveTab('description');
    }
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-orange-50">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-purple-100/50 rounded-t-lg transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <CardTitle className="text-lg">AI Writing Assistant</CardTitle>
                <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                  Beta
                </Badge>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            <CardDescription>
              Generate compelling descriptions and social posts with AI
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            {/* Usage indicator */}
            {usage && (
              <div className="mb-4 p-2 bg-white rounded-lg border border-purple-100">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Daily: {usage.daily.used}/{usage.daily.limit}</span>
                  <span>Monthly: {usage.monthly.used}/{usage.monthly.limit}</span>
                </div>
              </div>
            )}
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            
            {/* Tab buttons */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={activeTab === 'description' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('description')}
              >
                <Wand2 className="w-4 h-4 mr-1" />
                Description
              </Button>
              <Button
                variant={activeTab === 'social' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('social')}
              >
                <Sparkles className="w-4 h-4 mr-1" />
                Social Posts
              </Button>
            </div>
            
            {/* Description Generator */}
            {activeTab === 'description' && (
              <div className="space-y-4">
                {/* Quick actions */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleImproveExisting}
                    disabled={!currentDescription}
                  >
                    ✨ Improve Current
                  </Button>
                </div>
                
                {/* Bullet points input */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Enter bullet points about your event:
                  </label>
                  <Textarea
                    value={bulletPoints}
                    onChange={(e) => setBulletPoints(e.target.value)}
                    placeholder={`• 3-day technology trade show\n• 500+ exhibitors\n• Keynote speakers from Google, Microsoft\n• Free registration for trade visitors`}
                    rows={5}
                    className="font-mono text-sm"
                  />
                </div>
                
                {/* Options */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Tone</label>
                    <Select value={tone} onValueChange={setTone}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="exciting">Exciting</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Length</label>
                    <Select value={length} onValueChange={setLength}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Short (~75 words)</SelectItem>
                        <SelectItem value="medium">Medium (~150 words)</SelectItem>
                        <SelectItem value="long">Long (~250 words)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button
                  onClick={handleGenerateDescription}
                  disabled={generating || !bulletPoints.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate Description</>
                  )}
                </Button>
                
                {/* Generated result */}
                {generatedDescription && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Generated Description</span>
                      <span className="text-xs text-gray-500">{generatedDescription.split(' ').length} words</span>
                    </div>
                    <p className="text-gray-800 whitespace-pre-wrap text-sm">{generatedDescription}</p>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" onClick={handleUseDescription}>
                        <Check className="w-4 h-4 mr-1" /> Use This
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleGenerateDescription} disabled={generating}>
                        <RefreshCw className="w-4 h-4 mr-1" /> Regenerate
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleCopy(generatedDescription, 'desc')}>
                        {copied.desc ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Social Posts Generator */}
            {activeTab === 'social' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Generate ready-to-post content for LinkedIn and X/Twitter based on your event details.
                </p>
                
                <Button
                  onClick={handleGenerateSocial}
                  disabled={generatingSocial}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {generatingSocial ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate Social Posts</>
                  )}
                </Button>
                
                {socialPosts && (
                  <div className="space-y-4">
                    {/* LinkedIn */}
                    {socialPosts.linkedin && (
                      <div className="p-4 bg-white rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-700">LinkedIn</span>
                          <span className="text-xs text-gray-500">
                            {socialPosts.linkedin.characterCount} characters
                          </span>
                        </div>
                        <p className="text-gray-800 whitespace-pre-wrap text-sm">
                          {socialPosts.linkedin.post}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={() => handleCopy(socialPosts.linkedin.post, 'linkedin')}
                        >
                          {copied.linkedin ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                          Copy
                        </Button>
                      </div>
                    )}
                    
                    {/* Twitter */}
                    {socialPosts.twitter && (
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">X / Twitter</span>
                          <span className={`text-xs ${socialPosts.twitter.characterCount > 280 ? 'text-red-500' : 'text-gray-500'}`}>
                            {socialPosts.twitter.characterCount}/280
                          </span>
                        </div>
                        <p className="text-gray-800 whitespace-pre-wrap text-sm">
                          {socialPosts.twitter.post}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2"
                          onClick={() => handleCopy(socialPosts.twitter.post, 'twitter')}
                        >
                          {copied.twitter ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                          Copy
                        </Button>
                      </div>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateSocial}
                      disabled={generatingSocial}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" /> Regenerate All
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
